import React from 'react';
import {
  Box,
  Chip,
  Paper,
  Typography,
  useTheme,
} from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import LaunchIcon from '@mui/icons-material/Launch';
import CheckIcon from '@mui/icons-material/Check';
import { amber, green, orange } from '@mui/material/colors';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import OptimusButton from '../../common/library/Button/Button';
import { useAppSelector } from '../../store/hooks';
import { selectMemberInformation } from '../../store/slices/onboardingSlice';
import { SubscriptionTier } from '../../common/models/Subscription';
import { meetsContentMembership } from '../../common/utils/MembershipTierAccess';
import { useCustomEventTracking } from '../../common/useCustomEventTracking';
import { NavigationTarget, useNavigationUtils } from '../../common/utils/useNavigationUtils';
import { MYACCOUNT_MEMBERSHIPS_ROUTE } from '../../routes/namedRoutes';
import { TestIds } from '../../assets/testIds';
import { COACHING_CONSULT_URL, PILLAR_CALLS_URL } from '../../common/constants';

interface LiveCoachingContentProps {
  isPaidTier: boolean;
  onScheduleConsult: () => void;
  onTierCta: () => void;
}

interface FeatureProps {
  emphasisKey?: string;
  textKey: string;
}

function Feature({ emphasisKey, textKey }: FeatureProps) {
  const theme = useTheme();
  const { t } = useTranslation();

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
      <CheckIcon
        sx={{
          mt: theme.spacing(0.125),
          flexShrink: 0,
          color: green[800],
          fontSize: theme.spacing(2.25),
        }}
      />
      <Typography
        variant="bodyMd"
        sx={{
          color: theme.palette.grey[800],
          fontSize: { xs: theme.spacing(1.375), md: theme.spacing(1.75) },
          lineHeight: { xs: theme.spacing(1.875), md: theme.spacing(2.5) },
        }}
      >
        {emphasisKey && (
          <Box component="span" sx={{ fontWeight: 700 }}>
            {t(emphasisKey)}
            {' '}
          </Box>
        )}
        {t(textKey)}
      </Typography>
    </Box>
  );
}

export function LiveCoachingContent({
  isPaidTier,
  onScheduleConsult,
  onTierCta,
}: LiveCoachingContentProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const pillarCallsKey = isPaidTier ? 'roundtable' : 'edge';

  const cardBaseStyles = {
    position: 'relative',
    isolation: 'isolate',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    minHeight: { md: theme.spacing(63.25) },
    p: { xs: 3, md: 4 },
    borderRadius: theme.spacing(1.5),
    boxShadow: `0 ${theme.spacing(0.25)} ${theme.spacing(0.625)} ${theme.palette.effects.shadowInnerBorder}`,
    '& > :not([data-card-decoration])': { position: 'relative', zIndex: 1 },
  } as const;

  return (
    <Box
      data-testid={TestIds.coach.liveCoachingContainer}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: 2.5, md: 4 },
        pb: 2,
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'minmax(0, 1fr)',
            md: `minmax(0, 3fr) minmax(${theme.spacing(45)}, 2fr)`,
          },
          gap: { xs: 2, md: 3 },
          alignItems: 'stretch',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            ...cardBaseStyles,
            border: `${theme.spacing(0.125)} solid ${alpha(theme.palette.brand.primary, 0.32)}`,
            background: `linear-gradient(112deg, ${theme.palette.common.white} -20%, ${theme.palette.brand.surface} 42%, ${theme.palette.common.white} 145%)`,
          }}
        >
          <Box
            aria-hidden
            data-card-decoration
            data-testid={TestIds.coach.oneOnOneCardDecoration}
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: 0,
              pointerEvents: 'none',
            }}
          >
            <Box
              data-testid={TestIds.coach.oneOnOneCardDecorationCircle}
              sx={{
                position: 'absolute',
                width: theme.spacing(17.5),
                height: theme.spacing(17.5),
                borderRadius: '50%',
                top: theme.spacing(-8.5),
                left: theme.spacing(-4.25),
                backgroundColor: alpha(theme.palette.brand.secondary, 0.14),
              }}
            />
            <Box
              data-testid={TestIds.coach.oneOnOneCardDecorationCircle}
              sx={{
                position: 'absolute',
                width: theme.spacing(27.5),
                height: theme.spacing(27.5),
                borderRadius: '50%',
                right: theme.spacing(-11.25),
                bottom: theme.spacing(-13.5),
                backgroundColor: alpha(theme.palette.brand.secondary, 0.1),
              }}
            />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: { xs: theme.spacing(4.5), md: theme.spacing(6.5) },
                height: { xs: theme.spacing(4.5), md: theme.spacing(6.5) },
                flexShrink: 0,
                borderRadius: theme.spacing(1.5),
                color: theme.palette.common.white,
                backgroundColor: theme.palette.brand.primary,
              }}
            >
              <PersonOutlineIcon
                sx={{ fontSize: { xs: theme.spacing(2.75), md: theme.spacing(3.625) } }}
              />
            </Box>
            <Typography
              variant="headingSm"
              sx={{
                color: theme.palette.grey[900],
                fontSize: { xs: theme.spacing(2.25), md: theme.spacing(2.5) },
                lineHeight: { xs: theme.spacing(3), md: theme.spacing(3.5) },
              }}
            >
              {t('coach.liveCoaching.oneOnOne.title')}
            </Typography>
          </Box>

          <Chip
            icon={<CalendarMonthOutlinedIcon />}
            label={t('coach.liveCoaching.oneOnOne.badge')}
            variant="outlined"
            size="small"
            sx={{
              alignSelf: 'flex-start',
              mt: { xs: 2, md: 3 },
              height: { xs: theme.spacing(2.75), md: theme.spacing(3.75) },
              color: theme.palette.brand.primary,
              borderColor: theme.palette.brand.primary,
              backgroundColor: alpha(theme.palette.common.white, 0.5),
              '& .MuiChip-icon': {
                color: 'inherit',
                ml: 1,
                fontSize: { xs: theme.spacing(1.5), md: theme.spacing(1.875) },
              },
              '& .MuiChip-label': {
                paddingInline: theme.spacing(1.25),
                ...theme.typography.bodyMd,
                fontSize: { xs: theme.spacing(1.375), md: theme.spacing(1.75) },
              },
            }}
          />

          <Typography
            variant="bodyLg"
            sx={{
              mt: { xs: 2.25, md: 3.25 },
              color: theme.palette.grey[800],
              maxWidth: theme.spacing(81.25),
              minHeight: { md: theme.spacing(12) },
              fontSize: { xs: theme.spacing(1.5), md: theme.spacing(2) },
              lineHeight: { xs: theme.spacing(2), md: theme.spacing(3) },
            }}
          >
            <Box component="span" sx={{ fontWeight: 700 }}>
              {t('coach.liveCoaching.oneOnOne.descriptionLead')}
              {' '}
            </Box>
            {t('coach.liveCoaching.oneOnOne.description')}
          </Typography>

          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: 1, md: 1.5 },
            mt: { xs: 2, md: 3 },
          }}>
            <Feature
              emphasisKey="coach.liveCoaching.oneOnOne.features.dedicatedLead"
              textKey="coach.liveCoaching.oneOnOne.features.dedicated"
            />
            <Feature
              emphasisKey="coach.liveCoaching.oneOnOne.features.flexibleLead"
              textKey="coach.liveCoaching.oneOnOne.features.flexible"
            />
            <Feature
              emphasisKey="coach.liveCoaching.oneOnOne.features.actionableLead"
              textKey="coach.liveCoaching.oneOnOne.features.actionable"
            />
          </Box>

          <OptimusButton
            data-testid={TestIds.coach.scheduleConsultButton}
            buttonText={`${t('coach.liveCoaching.scheduleConsult')}`}
            leadingIcon={<CalendarMonthOutlinedIcon />}
            onClick={onScheduleConsult}
            size="m"
            sx={{
              mt: { xs: 2.25, md: 'auto' },
              height: { xs: theme.spacing(4), md: theme.spacing(5) },
              minHeight: { xs: theme.spacing(4), md: theme.spacing(5) },
              fontSize: { xs: theme.spacing(1.375), md: theme.spacing(1.75) },
              borderRadius: theme.spacing(1),
              backgroundColor: theme.palette.brand.primary,
              color: theme.palette.common.white,
            }}
          />
        </Paper>

        <Paper
          elevation={0}
          sx={{
            ...cardBaseStyles,
            border: `${theme.spacing(0.125)} solid ${amber[600]}`,
            background: `linear-gradient(112deg, ${amber[50]} 0%, ${orange[100]} 100%)`,
            '&::before': {
              content: '""',
              position: 'absolute',
              zIndex: 0,
              width: theme.spacing(17),
              height: theme.spacing(17),
              borderRadius: '50%',
              top: theme.spacing(-7.75),
              left: theme.spacing(-6.875),
              backgroundColor: alpha(theme.palette.common.white, 0.36),
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              zIndex: 0,
              width: theme.spacing(18.75),
              height: theme.spacing(18.75),
              borderRadius: '50%',
              right: theme.spacing(-6.875),
              bottom: theme.spacing(-7.875),
              backgroundColor: alpha(theme.palette.common.white, 0.42),
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: { xs: theme.spacing(4.5), md: theme.spacing(6.5) },
                height: { xs: theme.spacing(4.5), md: theme.spacing(6.5) },
                flexShrink: 0,
                borderRadius: theme.spacing(1.5),
                color: theme.palette.grey[900],
                backgroundColor: amber[300],
              }}
            >
              <GroupsOutlinedIcon
                sx={{ fontSize: { xs: theme.spacing(2.75), md: theme.spacing(3.625) } }}
              />
            </Box>
            <Typography
              variant="headingSm"
              sx={{
                color: theme.palette.grey[900],
                fontSize: { xs: theme.spacing(2.25), md: theme.spacing(2.5) },
                lineHeight: { xs: theme.spacing(3), md: theme.spacing(3.5) },
              }}
            >
              {t('coach.liveCoaching.pillarCalls.title')}
            </Typography>
          </Box>

          <Chip
            icon={<RocketLaunchOutlinedIcon />}
            label={t(`coach.liveCoaching.pillarCalls.${pillarCallsKey}.badge`)}
            variant="outlined"
            size="small"
            sx={{
              alignSelf: 'flex-start',
              mt: { xs: 2, md: 3 },
              height: { xs: theme.spacing(2.75), md: theme.spacing(3.75) },
              color: orange[800],
              borderColor: orange[700],
              backgroundColor: alpha(theme.palette.common.white, 0.38),
              '& .MuiChip-icon': {
                color: 'inherit',
                ml: 1,
                fontSize: { xs: theme.spacing(1.5), md: theme.spacing(1.875) },
              },
              '& .MuiChip-label': {
                paddingInline: theme.spacing(1.25),
                ...theme.typography.bodyMd,
                fontSize: { xs: theme.spacing(1.375), md: theme.spacing(1.75) },
              },
            }}
          />

          <Typography
            variant="bodyLg"
            sx={{
              mt: { xs: 2.25, md: 3.25 },
              color: theme.palette.grey[800],
              minHeight: { md: theme.spacing(12) },
              fontSize: { xs: theme.spacing(1.5), md: theme.spacing(2) },
              lineHeight: { xs: theme.spacing(2), md: theme.spacing(3) },
            }}
          >
            <Box component="span" sx={{ fontWeight: 700 }}>
              {t('coach.liveCoaching.pillarCalls.descriptionLead')}
              {' '}
            </Box>
            {t(`coach.liveCoaching.pillarCalls.${pillarCallsKey}.description`)}
          </Typography>

          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: 1, md: 1.5 },
            mt: { xs: 2, md: 3 },
          }}>
            {[0, 1, 2].map((featureIndex) => (
              <Feature
                key={featureIndex}
                textKey={`coach.liveCoaching.pillarCalls.${pillarCallsKey}.features.${featureIndex}`}
              />
            ))}
          </Box>

          <OptimusButton
            data-testid={TestIds.coach.tierCtaButton}
            buttonText={`${t(`coach.liveCoaching.pillarCalls.${pillarCallsKey}.cta`)}`}
            leadingIcon={isPaidTier ? <LaunchIcon /> : <RocketLaunchOutlinedIcon />}
            customVariant="secondaryColor"
            onClick={onTierCta}
            size="m"
            sx={{
              mt: { xs: 2.25, md: 'auto' },
              height: { xs: theme.spacing(4), md: theme.spacing(5) },
              minHeight: { xs: theme.spacing(4), md: theme.spacing(5) },
              fontSize: { xs: theme.spacing(1.375), md: theme.spacing(1.75) },
              borderRadius: theme.spacing(1),
              backgroundColor: amber[300],
              color: theme.palette.grey[900],
              '&:hover': {
                backgroundColor: amber[300],
              },
              '& .MuiTouchRipple-root': {
                color: theme.palette.common.white,
              },
              '&:active': {
                backgroundColor: amber[300],
              },
            }}
          />
        </Paper>
      </Box>

    </Box>
  );
}

export function LiveCoaching() {
  const navigate = useNavigate();
  const { openExternalLink } = useNavigationUtils();
  const { trackEngagement } = useCustomEventTracking();
  const { company } = useAppSelector(selectMemberInformation);
  const subscriptionTier = company?.subscription?.subscriptionTier;
  // Pillar Calls is a Roundtable benefit, so this asks about coaching entitlement, not
  // billing: Association pays yet reads Foundation content, and keeps the free variant.
  const isPaidTier = meetsContentMembership(subscriptionTier, 'Performance');
  // Report the member's own tier instead of collapsing every paid tier onto
  // Roundtable: more tiers are coming and the analytics have to tell them apart
  // (SN-996). Members with no subscription are Edge, as everywhere else.
  const membershipTier = subscriptionTier ?? SubscriptionTier.EDGE;

  const handleScheduleConsult = () => {
    trackEngagement('Coaching', 'Click', 'ScheduleConsult', {
      membershipTier,
    });
    openExternalLink(COACHING_CONSULT_URL, NavigationTarget.Browser);
  };

  const handleTierCta = () => {
    if (isPaidTier) {
      trackEngagement('Coaching', 'Click', 'LearnMore', { membershipTier });
      openExternalLink(PILLAR_CALLS_URL, NavigationTarget.Browser);
      return;
    }

    trackEngagement('Coaching', 'Click', 'Upgrade', { membershipTier });
    navigate(MYACCOUNT_MEMBERSHIPS_ROUTE);
  };

  return (
    <LiveCoachingContent
      isPaidTier={isPaidTier}
      onScheduleConsult={handleScheduleConsult}
      onTierCta={handleTierCta}
    />
  );
}
